import { z } from "zod";
import { BindingEnum } from "@/app/types";

const MAX_ENDPOINT_LENGTH = 1000;

const oauth2ConfigSchema = z.object({
  client_id: z.string().min(1, { message: "Client ID is required" }),
  client_secret: z.object({
    binding: BindingEnum,
    editable: z.boolean(),
    exists: z.boolean(),
  }),
  redirect_uri: z.string(),
  // Optional legacy redirect URI flags/values (returned by server for edit UX)
  allow_legacy_redirect_uri: z.boolean().optional(),
  use_legacy_redirect_uri: z.boolean().optional(),
  legacy_redirect_uri: z.string().optional(),
});

// Base request configuration
const BaseRequestConfigSchema = z.object({
  endpoint: z.string().min(1, { message: "Endpoint is required" }),
  auth_method: z
    .enum(["client_secret_basic", "bearer_access_token"])
    .optional(),
  // method: z.enum(['GET', 'POST']).optional(),
  params: z.record(z.string(), z.string()).optional(),
  response_content_type: z
    .enum(["application/x-www-form-urlencoded", "application/json"])
    .optional(),
  response_map: z.record(z.string(), z.string()).optional().nullable(),
});

// OAuth response configuration schema with enforced response map keys
const EnforcedResponseMapConfigSchema = BaseRequestConfigSchema.extend({
  response_map: z
    .record(z.string(), z.string())
    .refine(
      (map) => {
        if (!map) {
          return true; // Allow null/undefined
        }
        return Object.keys(map).every((key) =>
          SUPPORTED_RESPONSE_MAP_KEYS.includes(
            // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
            key as (typeof SUPPORTED_RESPONSE_MAP_KEYS)[number]
          )
        );
      },
      {
        message:
          "Response map can only contain the following keys: access_token, expires_in, refresh_token, scope, token_type",
      }
    )
    .optional()
    .nullable(),
});

// Base response pagination schema
const PaginatedResponseSchema = z.object({
  limit: z.number(),
  offset: z.number(),
  page_count: z.number(),
  total_count: z.number(),
});

// OAuth provider item schema
const OAuthProviderItemSchema = z.object({
  id: z.string().min(1, { message: "ID is required" }),
  provider_id: z.string(),
  type: z.literal("oauth2"),
  binding: z.object({
    type: BindingEnum,
  }),
  created_at: z.string(),
  updated_at: z.string(),
  description: z.string().optional(),
  status: z.enum(["active", "inactive"]).default("active"),
  oauth2: oauth2ConfigSchema,
});
export type OAuthProviderItem = z.infer<typeof OAuthProviderItemSchema>;

// Response schema with pagination
const OAuthProviderResponseSchema = PaginatedResponseSchema.extend({
  items: z.array(OAuthProviderItemSchema),
});
export type OAuthProviderResponse = z.infer<typeof OAuthProviderResponseSchema>;

// Connected account item schema
const ConnectedAccountItemSchema = z.object({
  id: z.string(),
  connection_id: z.string(),
  connection_status: z.string(),
  scopes: z.array(z.string()),
  user_id: z.string(),
  provider_id: z.string(),
  provider_type: z.string().optional(),
  provider_description: z.string(),
  provider_user_info: z.record(z.string(), z.unknown()),
  binding: z.object({
    type: BindingEnum,
    id: z.string(),
  }),
});
export type ConnectedAccountItem = z.infer<typeof ConnectedAccountItemSchema>;

// Connected accounts response schema
const ConnectedAccountsResponseSchema = PaginatedResponseSchema.extend({
  items: z.array(ConnectedAccountItemSchema),
});
export type ConnectedAccountsResponse = z.infer<
  typeof ConnectedAccountsResponseSchema
>;

// User Info Request Triggers Schema
const UserInfoRequestTriggersSchema = z.object({
  on_token_grant: z.boolean().optional(),
  on_token_refresh: z.boolean().optional(),
});

// User Info Request Configuration Schema - extends BaseRequestConfig
const UserInfoRequestConfigSchema = BaseRequestConfigSchema.extend({
  triggers: UserInfoRequestTriggersSchema.optional(),
});

const SUPPORTED_RESPONSE_MAP_KEYS = [
  "access_token",
  "expires_in",
  "refresh_token",
  "scope",
  "token_type",
] as const;

// Token Introspection Request Configuration Schema
const TokenIntrospectionCreateRequestSchema = z
  .object({
    enabled: z.boolean().optional().default(false),
    endpoint: z.string().max(MAX_ENDPOINT_LENGTH).optional(),
    method: z.enum(["GET", "POST"]).optional().default("GET"),
    params: z.record(z.string(), z.string()).optional(),
    auth_method: z
      .enum(["client_secret_basic", "bearer_access_token"])
      .optional()
      .default("client_secret_basic"),
    request_content_type: z
      .enum(["application/x-www-form-urlencoded", "application/json"])
      .optional()
      .default("application/x-www-form-urlencoded"),
    response_content_type: z
      .enum(["application/x-www-form-urlencoded", "application/json"])
      .optional()
      .default("application/json"),
    response_map: z
      .record(z.string(), z.string())
      .refine(
        (map) => {
          if (!map) {
            return true; // Allow null/undefined
          }
          return Object.keys(map).every((key) =>
            SUPPORTED_RESPONSE_MAP_KEYS.includes(
              // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
              key as (typeof SUPPORTED_RESPONSE_MAP_KEYS)[number]
            )
          );
        },
        {
          message:
            "Response map can only contain the following keys: access_token, expires_in, refresh_token, scope, token_type",
        }
      )
      .optional()
      .nullable(),
    expiration_format: z
      .enum(["absolute_unix_timestamp", "relative_seconds"])
      .optional()
      .default("absolute_unix_timestamp"),
    triggers: z
      .object({
        on_token_grant: z.boolean().optional().default(false),
        on_token_refresh: z.boolean().optional().default(false),
      })
      .optional(),
  })
  .refine(
    (data) => {
      if (!data.enabled) {
        return true;
      }
      return (
        !!data.endpoint &&
        !!data.triggers &&
        (data.triggers.on_token_grant || data.triggers.on_token_refresh)
      );
    },
    {
      message:
        "Endpoint and at least one trigger are required when token introspection is enabled",
      path: ["endpoint"],
    }
  );

// PKCE Configuration Schema
const PKCEConfigSchema = z.object({
  enabled: z.boolean().optional(),
  code_challenge_method: z.literal("S256").optional(),
});

// Oauth2 Prebuilt Configuration Schema
const OAuth2ConfigSchemaPrebuilt = z.object({
  client_id: z.string().min(1, { message: "Client ID is required" }),
  client_secret: z.string().optional().default(""),
});

// OAuth2 Configuration Schema
const OAuth2ConfigSchemaCustom = OAuth2ConfigSchemaPrebuilt.extend({
  scope_delimiter: z.enum([",", " "]).optional(),
  pkce: PKCEConfigSchema.optional(),
  authorize_request: BaseRequestConfigSchema,
  token_request: EnforcedResponseMapConfigSchema.optional(),
  refresh_request: EnforcedResponseMapConfigSchema.optional(),
  user_info_request: UserInfoRequestConfigSchema.optional(),
  token_introspection_request: TokenIntrospectionCreateRequestSchema.optional(),
});

// Auth Provider Schema
const AuthProviderSchemaBase = z.object({
  id: z
    .string()
    .min(1, { message: "ID is required" })
    .refine((val) => !val.toLowerCase().includes("arcade"), {
      message: 'ID cannot contain "arcade". This word is reserved.',
    }),
  description: z.string().optional(),
  status: z.enum(["active", "inactive"]).optional(),
  provider_id: z.string().optional(),
  type: z.literal("oauth2").optional(),
  external_id: z.string(),
  binding: z
    .object({
      type: BindingEnum,
    })
    .default({ type: "project" }),
});

export const AuthProviderSchemaPrebuilt = AuthProviderSchemaBase.extend({
  oauth2: OAuth2ConfigSchemaPrebuilt,
});
export type AuthProviderPrebuilt = z.infer<typeof AuthProviderSchemaPrebuilt>;

export const AuthProviderSchemaCustom = AuthProviderSchemaBase.extend({
  oauth2: OAuth2ConfigSchemaCustom,
});
export type AuthProviderCustom = z.infer<typeof AuthProviderSchemaCustom>;
export type AuthProviderCustomForm = z.input<typeof AuthProviderSchemaCustom>;

export type AuthProvider = AuthProviderPrebuilt | AuthProviderCustom;

// OAuth2 Config Update Request Schema
const OAuth2ConfigUpdateRequestSchema = z.object({
  client_id: z.string().optional(),
  client_secret: z.string().optional(),
  // Allow updating the legacy redirect toggle in edit mode
  use_legacy_redirect_uri: z.boolean().optional(),
});

// Auth Provider Update Request Schema
const AuthProviderUpdateRequestSchema = z.object({
  id: z.string().optional(),
  description: z.string().optional(),
  provider_id: z.string().optional(),
  status: z.string().optional(),
  type: z.string().optional(),
  oauth2: OAuth2ConfigUpdateRequestSchema.optional(),
});

export type AuthProviderUpdateRequest = z.infer<
  typeof AuthProviderUpdateRequestSchema
>;

export const defaultValues: Partial<AuthProvider> = {
  id: "",
  type: "oauth2",
  status: "active",
  binding: {
    type: "project",
  },
  oauth2: {
    client_id: "",
    client_secret: "",
    // Default scope delimiter is a space
    scope_delimiter: " ",

    // Default PKCE settings
    pkce: {
      enabled: false,
      code_challenge_method: "S256",
    },

    // Default authorize request parameters
    authorize_request: {
      endpoint: "",
      params: {
        response_type: "code",
        client_id: "{{client_id}}",
        redirect_uri: "{{redirect_uri}}",
        scope: "{{existing_scopes}} {{scopes}}",
      },
      response_map: null,
    },

    // Default token request settings
    token_request: {
      endpoint: "",
      auth_method: "client_secret_basic",
      response_content_type: "application/json",
      params: {
        grant_type: "authorization_code",
        redirect_uri: "{{redirect_uri}}",
        client_id: "{{client_id}}",
        client_secret: "{{client_secret}}",
      },
      response_map: null,
    },

    // Default refresh request settings
    refresh_request: {
      endpoint: "",
      auth_method: "client_secret_basic",
      response_content_type: "application/json",
      params: {
        grant_type: "refresh_token",
        refresh_token: "{{refresh_token}}",
        client_id: "{{client_id}}",
        client_secret: "{{client_secret}}",
      },
      response_map: null,
    },

    // Default token introspection settings
    token_introspection_request: {
      enabled: false,
      endpoint: "",
      method: "GET",
      auth_method: "client_secret_basic",
      request_content_type: "application/x-www-form-urlencoded",
      response_content_type: "application/json",
      expiration_format: "absolute_unix_timestamp",
      params: {},
      response_map: null,
      triggers: {
        on_token_grant: false,
        on_token_refresh: false,
      },
    },
  },
};
